import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TrialCard from '../components/TrialCard';
import TrialDetailDrawer from '../components/TrialDetailDrawer';
import TrialFilters from '../components/TrialFilters';
import {
  filterTrials,
  getMatchedTrialsForPatient,
  saveTrial,
  unsaveTrial,
} from '../services/trialService';
import { summarizeTrialsForChatbot, buildMapsUrl, getUniqueValues } from '../utils/trialHelpers';
import { buildDiagnosisProfile } from '../utils/trialMatching';

const DEFAULT_FILTERS = {
  distance: '',
  cancerType: '',
  subtype: '',
  stage: '',
  biomarker: '',
  recruitingStatus: '',
  phase: '',
  location: '',
  savedOnly: false,
};

export default function ClinicalTrials({ user, userProfile, onOpenChat }) {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const diagnosisProfile = useMemo(
    () => buildDiagnosisProfile(userProfile, user),
    [userProfile, user]
  );

  const loadTrials = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const matched = await getMatchedTrialsForPatient({
        username: user?.username,
        diagnosisProfile,
        filters,
      });
      setTrials(Array.isArray(matched) ? matched : []);
    } catch {
      setError('Unable to load clinical trials right now. Showing available cached results if possible.');
    } finally {
      setLoading(false);
    }
  }, [diagnosisProfile, filters, user?.username]);

  useEffect(() => {
    loadTrials();
  }, [loadTrials]);

  const filteredTrials = useMemo(() => filterTrials(trials, filters), [trials, filters]);

  const options = useMemo(() => ({
    cancerTypes: getUniqueValues(trials, 'cancerType'),
    subtypes: getUniqueValues(trials, 'subtype'),
    stages: getUniqueValues(trials, 'stage'),
    biomarkers: getUniqueValues(trials.flatMap((t) => t.biomarkers || []).map((b) => ({ value: b })), 'value'),
    statuses: getUniqueValues(trials, 'recruitingStatus'),
    phases: getUniqueValues(trials, 'phase'),
  }), [trials]);

  const handleToggleSave = async (trial) => {
    const targetSaved = !trial.isSaved;
    if (targetSaved) {
      await saveTrial(user?.username, trial.id);
    } else {
      await unsaveTrial(user?.username, trial.id);
    }

    setTrials((prev) => prev.map((item) => (
      item.id === trial.id ? { ...item, isSaved: targetSaved } : item
    )));

    if (selectedTrial?.id === trial.id) {
      setSelectedTrial((prev) => (prev ? { ...prev, isSaved: targetSaved } : prev));
    }
  };

  const handleOpenMaps = (trial) => {
    const mapsUrl = buildMapsUrl(trial);
    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };

  const handleChatWithTrialContext = () => {
    const summary = summarizeTrialsForChatbot(filteredTrials, { savedOnly: Boolean(filters.savedOnly) });
    localStorage.setItem('trialContext', summary);
    if (onOpenChat) onOpenChat(summary);
  };

  const matchedCount = filteredTrials.filter((t) => t.isMatched).length;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', paddingBottom: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', color: '#0f172a' }}>Clinical Trials</h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
            {filteredTrials.length} trial match{filteredTrials.length === 1 ? '' : 'es'} • {matchedCount} relevant to you
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => setViewMode('list')} style={viewMode === 'list' ? activeChip : idleChip}>List View</button>
          <button type="button" onClick={() => setViewMode('location')} style={viewMode === 'location' ? activeChip : idleChip}>Location View</button>
          <button
            type="button"
            onClick={() => setFilters((prev) => ({ ...prev, savedOnly: !prev.savedOnly }))}
            style={filters.savedOnly ? activeChip : idleChip}
          >
            Saved
          </button>
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            style={filtersOpen ? activeChip : idleChip}
          >
            {filtersOpen ? 'Hide Filters' : 'Filters'}
          </button>
        </div>
      </div>

      {filtersOpen && (
        <TrialFilters
          filters={filters}
          onChange={setFilters}
          onClear={() => setFilters(DEFAULT_FILTERS)}
          options={options}
        />
      )}

      {error && (
        <div style={errorCard}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={stateCard}>Loading matched clinical trials...</div>
      ) : filteredTrials.length === 0 ? (
        <div style={stateCard}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>No trials match these filters</div>
          <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>
            Try clearing filters or broadening location, stage, or biomarker criteria.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: viewMode === 'location' ? '1fr' : '1fr', gap: '10px' }}>
          {filteredTrials.map((trial) => (
            <TrialCard
              key={trial.id}
              trial={trial}
              mode={viewMode}
              onViewDetails={setSelectedTrial}
              onToggleSave={handleToggleSave}
              onOpenMaps={handleOpenMaps}
            />
          ))}
        </div>
      )}

      {filteredTrials.length > 0 && onOpenChat && (
        <div style={{ marginTop: '14px' }}>
          <button type="button" onClick={handleChatWithTrialContext} style={chatBtn}>
            Discuss matched trials with Reaxano AI
          </button>
        </div>
      )}

      <TrialDetailDrawer
        trial={selectedTrial}
        onClose={() => setSelectedTrial(null)}
        onToggleSave={handleToggleSave}
        onOpenMaps={handleOpenMaps}
      />
    </div>
  );
}

const stateCard = {
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '28px 16px',
  textAlign: 'center',
  color: '#64748b',
  fontSize: '14px',
};

const errorCard = {
  marginBottom: '10px',
  padding: '11px',
  borderRadius: '8px',
  border: '1px solid #fecaca',
  background: '#fef2f2',
  color: '#991b1b',
  fontSize: '13px',
};

const activeChip = {
  border: '1px solid var(--teal)',
  background: 'var(--brand-primary-light)',
  color: 'var(--teal-dark)',
  borderRadius: '999px',
  padding: '7px 11px',
  fontSize: '12px',
  fontWeight: '700',
  cursor: 'pointer',
};

const idleChip = {
  border: '1px solid #d1d5db',
  background: 'white',
  color: '#334155',
  borderRadius: '999px',
  padding: '7px 11px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};

const chatBtn = {
  width: '100%',
  border: '1px solid var(--teal)',
  background: 'white',
  color: 'var(--teal)',
  borderRadius: '10px',
  padding: '11px 12px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
};
